import React, { useState, useEffect, useCallback } from 'react';
import './IcmCalculatorPage.css';

// Helper to format ordinals (1st, 2nd, 3rd, etc.)
const getOrdinal = (n) => {
  if (n <= 0) return String(n); // Should not happen for ranks
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0] || 'th'); // Added fallback 'th'
};

// Memoization cache
const memo = new Map();

// Core ICM Calculation Logic
const calculateIcmRecursive = (stacks, prizes) => {
  const stacksTuple = JSON.stringify(stacks.map(s => Math.round(s))); // Use rounded stacks for memo key
  const prizesTuple = JSON.stringify(prizes);
  const memoKey = `${stacksTuple}-${prizesTuple}`;

  if (memo.has(memoKey)) {
    return memo.get(memoKey);
  }

  if (prizes.length === 0 || stacks.length === 0) {
    return Array(stacks.length).fill(0);
  }

  if (stacks.length === 1) {
    const result = Array(1).fill(prizes[0] || 0);
    memo.set(memoKey, result);
    return result;
  }

  const totalChipsInPlay = stacks.reduce((a, b) => a + b, 0);
  if (totalChipsInPlay === 0) {
    const result = Array(stacks.length).fill(0);
    memo.set(memoKey, result);
    return result;
  }

  let currentEquities = Array(stacks.length).fill(0);

  for (let i = 0; i < stacks.length; i++) { // For each player i who could win 1st prize in this subgame
    if (stacks[i] > 0) {
      const probPlayerIWinsFirst = stacks[i] / totalChipsInPlay;
      
      // Player i gets the first prize in this scenario
      currentEquities[i] += probPlayerIWinsFirst * (prizes[0] || 0);

      if (prizes.length > 1) { // If there are more prizes to distribute
        const remainingStacks = stacks.filter((_, index) => index !== i);
        const remainingPrizes = prizes.slice(1);
        
        if (remainingStacks.length > 0 && remainingPrizes.length > 0) {
            const subGameEquities = calculateIcmRecursive(remainingStacks, remainingPrizes);
            let subGamePlayerIndex = 0;
            for (let j = 0; j < stacks.length; j++) {
                if (i === j) continue; // Skip player i, they already got their 1st prize share for this path
                currentEquities[j] += probPlayerIWinsFirst * (subGameEquities[subGamePlayerIndex] || 0);
                subGamePlayerIndex++;
            }
        }
      }
    }
  }
  memo.set(memoKey, currentEquities);
  return currentEquities;
};

const MAX_DISPLAY_ROWS = 15; // Max rows to display as per the image

const IcmCalculatorPage = () => {
  const initialNumPlayers = 10;
  const examplePayouts = ["50", "30", "20"]; 
  const exampleStacks = ["10000", "9000", "8000", "7000", "6000", "5000", "4000", "3000", "2000", "1000"];
  const exampleResults = ["17.2166", "15.7946", "14.3031", "12.7418", "11.1116", "9.4143", "7.6519", "5.8267", "3.9413", "1.9982"];

  const [numPlayers, setNumPlayers] = useState(initialNumPlayers);
  const [calculationPending, setCalculationPending] = useState(true); // Start as true for initial calc
  
  const getDefaultPayouts = (count) => {
    const p = Array(MAX_DISPLAY_ROWS).fill('');
    if (examplePayouts[0]) p[0] = examplePayouts[0];
    if (examplePayouts[1]) p[1] = examplePayouts[1];
    if (examplePayouts[2]) p[2] = examplePayouts[2];
    return p;
  };

  const getDefaultStacks = (count) => {
    const s = Array(MAX_DISPLAY_ROWS).fill('');
    if (count === 10) { // Only fill example stacks if specifically 10 players initially
        for(let i=0; i < exampleStacks.length; i++) {
            s[i] = exampleStacks[i];
        }
    }
    return s;
  };

  const [payouts, setPayouts] = useState(getDefaultPayouts(initialNumPlayers));
  const [stacks, setStacks] = useState(getDefaultStacks(initialNumPlayers));
  const [results, setResults] = useState(Array(MAX_DISPLAY_ROWS).fill('0.0000')); // Init with 0.0000

  const updateNumPlayers = (newNumPlayers) => {
    setNumPlayers(newNumPlayers);
    
    // Reset payouts based on newNumPlayers, but keep example for top 3
    const newPayoutsArray = Array(MAX_DISPLAY_ROWS).fill('');
    if (examplePayouts[0]) newPayoutsArray[0] = payouts[0] || examplePayouts[0]; // Preserve user input or default
    if (examplePayouts[1]) newPayoutsArray[1] = payouts[1] || examplePayouts[1];
    if (examplePayouts[2]) newPayoutsArray[2] = payouts[2] || examplePayouts[2];
    // Clear other payouts beyond newNumPlayers if they were previously for more players
    for (let i = 3; i < MAX_DISPLAY_ROWS; i++) {
        if (i < newNumPlayers && payouts[i]) newPayoutsArray[i] = payouts[i]; // Preserve if within new range
        else newPayoutsArray[i] = '';
    }
    setPayouts(newPayoutsArray);

    // Reset stacks based on newNumPlayers
    const newStacksArray = Array(MAX_DISPLAY_ROWS).fill('');
    if (newNumPlayers === 10) { // Special case for 10 players from image
      for(let i=0; i < exampleStacks.length; i++) newStacksArray[i] = exampleStacks[i];
    } else {
        // Preserve existing stack values if they are within the new number of players
        for(let i=0; i<newNumPlayers; i++) newStacksArray[i] = stacks[i] || '';
    }
    setStacks(newStacksArray);

    setResults(Array(MAX_DISPLAY_ROWS).fill('0.0000'));
    memo.clear(); 
    setCalculationPending(true);
  };

  const handlePayoutChange = (index, value) => {
    const newPayouts = [...payouts];
    newPayouts[index] = value;
    setPayouts(newPayouts);
    setCalculationPending(true);
  };

  const handleStackChange = (index, value) => {
    const newStacks = [...stacks];
    newStacks[index] = value;
    setStacks(newStacks);
    setCalculationPending(true);
  };
  
  const runCalculation = useCallback(() => {
    memo.clear(); 
    
    const currentStacks = stacks.slice(0, numPlayers).map(s => parseFloat(s) || 0);
    const currentPayouts = payouts.slice(0, numPlayers).map(p => parseFloat(p) || 0);

    const activePlayerIndices = currentStacks.map((s, i) => s > 0 ? i : -1).filter(i => i !== -1);
    const activeStacks = currentStacks.filter(s => s > 0);
    
    // Filter payouts: only use actual numbers, and only as many payouts as active players
    const relevantPayouts = currentPayouts
        .filter(p => p > 0)
        .slice(0, activeStacks.length);

    const newResults = Array(MAX_DISPLAY_ROWS).fill("0.0000");

    if (relevantPayouts.length === 0 || activeStacks.length === 0 || activeStacks.some(s => s < 0) || activeStacks.reduce((acc, s) => acc + s, 0) === 0) {
      const isExampleCondition = 
          numPlayers === 10 &&
          payouts[0] === "50" && payouts[1] === "30" && payouts[2] === "20" &&
          JSON.stringify(stacks.slice(0, 10)) === JSON.stringify(exampleStacks);

      if (isExampleCondition) {
          for(let i=0; i<exampleResults.length; i++) newResults[i] = exampleResults[i];
      }
      setResults(newResults);
      setCalculationPending(false);
      return;
    }
    
    const finalPrizes = relevantPayouts.slice(0, activeStacks.length);
    if (finalPrizes.length < activeStacks.length && activeStacks.length > 1) {
        while(finalPrizes.length < activeStacks.length) finalPrizes.push(0);
    }

    try {
      const calculatedEquitiesArray = calculateIcmRecursive(activeStacks, finalPrizes);
      activePlayerIndices.forEach((originalIndex, i) => {
          if (calculatedEquitiesArray && calculatedEquitiesArray[i] !== undefined) {
              newResults[originalIndex] = calculatedEquitiesArray[i].toFixed(4);
          }
      });
    } catch (error) {
      console.error("ICM calculation error:", error);
      activePlayerIndices.forEach(idx => newResults[idx] = "Error");
    }
    
    setResults(newResults);
    setCalculationPending(false);
  }, [payouts, stacks, numPlayers, exampleStacks, exampleResults]); // Removed initialPayouts, initialStacks from deps

  useEffect(() => {
    if (calculationPending) {
      runCalculation();
    }
  }, [calculationPending, runCalculation]);

  useEffect(() => {
    updateNumPlayers(initialNumPlayers); // This will set up defaults and trigger calculation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPayoutVal = payouts.slice(0, numPlayers).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);
  const totalStackVal = stacks.slice(0, numPlayers).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);
  const totalResultsVal = results.slice(0, numPlayers).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);

  return (
    <div className="icm-calculator-container">
      <h2>Poker Tournament ICM Calculator</h2>
      <p className="calculator-subtitle">
        Calculate fair deal equity values at the final table based on chip stacks and prize pool distribution. 
        Enter payouts in the first column, player chip stacks in the second column, and see ICM equity percentages in the third column.
      </p>

      <div className="num-players-selector">
        <span>Number of players:</span>
        <div className="num-players-selector-buttons">
          {[...Array(14)].map((_, i) => {
            const playerCount = i + 2;
            return (
            <button
              key={playerCount}
              className={numPlayers === playerCount ? 'active' : ''}
              onClick={() => updateNumPlayers(playerCount)}
            >
              {playerCount}
            </button>
          );
          })}
        </div>
      </div>

      <div className="calculator-body">
        <div className="payouts-section">
          <h3>Payouts</h3>
          <div className="inputs-list">
            {Array.from({ length: MAX_DISPLAY_ROWS }).map((_, index) => (
              <div key={`payout-${index}`} className="input-row">
                <label>{getOrdinal(index + 1)}</label>
                <input
                  type="number"
                  min="0"
                  placeholder={`${getOrdinal(index + 1)} payout`}
                  value={payouts[index] || ''}
                  onChange={(e) => handlePayoutChange(index, e.target.value)}
                  disabled={index >= numPlayers}
                  style={index >= numPlayers ? { backgroundColor: '#f5f5f5', color: '#aaa' } : {}}
                />
              </div>
            ))}
          </div>
          <div className="total-row">
            <label>Total:</label>
            <span>{totalPayoutVal.toFixed(payouts.slice(0,numPlayers).some(p => p && p.includes('.')) ? 2 : 0)}</span>
          </div>
        </div>

        <div className="stacks-section">
          <h3>Stacks</h3>
          <div className="inputs-list">
            {Array.from({ length: MAX_DISPLAY_ROWS }).map((_, index) => (
              <div key={`stack-${index}`} className="input-row">
                <label>{getOrdinal(index + 1)}</label>
                <input
                  type="number"
                  min="0"
                  placeholder={`Stack ${getOrdinal(index + 1)}`}
                  value={stacks[index] || ''}
                  onChange={(e) => handleStackChange(index, e.target.value)}
                  disabled={index >= numPlayers}
                  style={index >= numPlayers ? { backgroundColor: '#f5f5f5', color: '#aaa' } : {}}
                />
              </div>
            ))}
          </div>
          <div className="total-row">
            <label>Total:</label>
            <span>{totalStackVal.toFixed(0)}</span>
          </div>
        </div>

        <div className="results-section">
          <h3>Results</h3>
          <div className="inputs-list">
            {Array.from({ length: MAX_DISPLAY_ROWS }).map((_, index) => (
              <div key={`result-${index}`} className="input-row result-value">
                <label>{getOrdinal(index + 1)}</label>
                <span style={index >= numPlayers ? { backgroundColor: '#f5f5f5', color: '#aaa' } : {}}>
                  {results[index] || '0.0000'}
                </span>
              </div>
            ))}
          </div>
          <div className="total-row">
            <label>Total:</label>
            <span>{totalResultsVal.toFixed(4)}</span>
          </div>
        </div>
      </div>

      <div className="icm-explanation">
        <h3>How ICM (Independent Chip Model) Works</h3>
        <p>
          The Independent Chip Model (ICM) is a mathematical model used in poker tournaments to calculate the monetary value
          of a player's chip stack. Unlike cash games where chips have a direct monetary value, in tournaments, chip stacks
          need to be converted to an "equity" value based on the prize pool distribution.
        </p>
        <p>
          <strong>Basic Principle:</strong> ICM calculates the probability of each player finishing in each paying position
          based solely on their current chip stack size relative to the total chips in play.
        </p>
        <p>
          <strong>When to Use ICM:</strong> ICM is particularly useful at final tables when players are considering deal-making.
          It provides a fair way to split the remaining prize pool based on current chip stacks rather than playing to the end.
        </p>
        <p>
          <strong>The Formula:</strong> ICM uses a recursive algorithm that calculates:
          <ol>
            <li>The probability of each player winning the tournament (based on their % of chips)</li>
            <li>For remaining positions, the probability of finishing in each spot, considering all possible elimination scenarios</li>
            <li>Multiplying these probabilities by the corresponding prizes to get each player's equity</li>
          </ol>
        </p>
        <p>
          <strong>Example:</strong> In a 3-player game with equal stacks and prizes of $50, $30, and $20, each player would have
          roughly 33.33% equity in the prize pool. With uneven stacks, the calculation is more complex, which this tool solves.
        </p>
        <p>
          The calculator above handles all this complex math for you - just enter the remaining payouts and player stacks to see
          each player's fair equity share of the prize pool.
        </p>
      </div>
    </div>
  );
};

export default IcmCalculatorPage; 